import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for cursor-based navigation with hover zones
 * Detects when cursor enters left/right 10% of screen and triggers navigation
 * 
 * @param {Function} onNavigate - Callback function (direction: 'prev' | 'next') => void
 * @param {boolean} enabled - Whether navigation is enabled
 * @param {number} hoverDelay - Delay in ms before triggering navigation (default: 800ms)
 * @returns {Object} { isInLeftZone, isInRightZone, cursorPosition }
 */
export const useCursorNavigation = (onNavigate, enabled = true, hoverDelay = 800) => {
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [isInLeftZone, setIsInLeftZone] = useState(false);
  const [isInRightZone, setIsInRightZone] = useState(false);
  const hoverTimerRef = useRef(null);
  const lastNavigationRef = useRef(0);
  const NAVIGATION_COOLDOWN = 1500; // Prevent rapid navigation

  const handleMouseMove = useCallback((e) => {
    if (!enabled) return;

    const { clientX, clientY } = e;
    const windowWidth = window.innerWidth;
    const leftZoneWidth = windowWidth * 0.1; // Left 10%
    const rightZoneStart = windowWidth * 0.9; // Right 10%

    setCursorPosition({ x: clientX, y: clientY });

    const inLeftZone = clientX <= leftZoneWidth;
    const inRightZone = clientX >= rightZoneStart;

    setIsInLeftZone(inLeftZone);
    setIsInRightZone(inRightZone);

    // Clear existing timer
    if (hoverTimerRef?.current) {
      clearTimeout(hoverTimerRef?.current);
      hoverTimerRef.current = null;
    }

    // Set new timer if in navigation zone
    if ((inLeftZone || inRightZone) && enabled) {
      hoverTimerRef.current = setTimeout(() => {
        const now = Date.now();
        const timeSinceLastNav = now - lastNavigationRef?.current;

        // Only navigate if cooldown period has passed
        if (timeSinceLastNav >= NAVIGATION_COOLDOWN) {
          const direction = inLeftZone ? 'prev' : 'next';
          console.log('Cursor navigation triggered:', direction);
          if (typeof onNavigate === 'function') {
            onNavigate(direction);
          }
          lastNavigationRef.current = now;
          
          // Reset zones after navigation
          setIsInLeftZone(false);
          setIsInRightZone(false);
        }
      }, hoverDelay);
    }
  }, [enabled, hoverDelay, onNavigate]);

  const handleMouseLeave = useCallback(() => {
    // Clear timer when mouse leaves window
    if (hoverTimerRef?.current) {
      clearTimeout(hoverTimerRef?.current);
      hoverTimerRef.current = null;
    }
    setIsInLeftZone(false);
    setIsInRightZone(false);
  }, []);

  useEffect(() => {
    if (!enabled) {
      console.log('Cursor navigation disabled');
      return;
    }

    console.log('Cursor navigation enabled');
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
      if (hoverTimerRef?.current) {
        clearTimeout(hoverTimerRef?.current);
      }
    };
  }, [enabled, handleMouseMove, handleMouseLeave]);

  return {
    isInLeftZone,
    isInRightZone,
    cursorPosition
  };
};

export default useCursorNavigation;